import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpParams,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { environment } from '@env/environment';
import {
  map,
  catchError,
  switchMap,
  tap,
  concatMap,
  retry,
} from 'rxjs/operators';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { AlertService } from './alert.service';
import { ErrorService } from './error.service';
import { AuthService } from '@auth0/auth0-angular';
import * as Sentry from '@sentry/angular-ivy';
import { StorageService } from './storage.service';
import { AuthManagerService } from './auth-manager.service';

/**
 * Service responsible for handling API requests
 * Provides methods for different HTTP operations
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private httpClient: HttpClient,
    public alertService: AlertService,
  ) {}

  /**
   * Get a JSON file from local assets
   * @param name JSON filename in assets
   * @returns Observable with the JSON content
   */
  getJson<T = any>(name: string): Observable<T> {
    return this.httpClient.get<T>(`assets/json/${name}.json`);
  }

  /**
   * Perform a GET request
   * @param url Endpoint URL
   * @param params Query parameters
   * @returns Observable with the response
   */
  get<T = any>(url: string, params?: HttpParams): Observable<T> {
    return this.httpClient.get<T>(url, { params: params });
  }

  /**
   * Perform a POST request
   * @param url Endpoint URL
   * @param data Data to send
   * @param options Additional options
   * @returns Observable with the response
   */
  post<T = any, D = any>(url: string, data: D, options?: any): Observable<T> {
    // Use type assertion to ensure correct return type
    return this.httpClient.post(url, data, options) as Observable<T>;
  }

  /**
   * Perform a PATCH request
   * @param url Endpoint URL
   * @param data Data to send
   * @param options Additional options
   * @returns Observable with the response
   */
  patch<T = any, D = any>(url: string, data: D, options?: any): Observable<T> {
    // Use type assertion to ensure correct return type
    return this.httpClient.patch(url, data, options) as Observable<T>;
  }

  /**
   * Perform a PUT request
   * @param url Endpoint URL
   * @param data Data to send
   * @returns Observable with the response
   */
  put<T = any, D = any>(url: string, data: D): Observable<T> {
    // Use type assertion to ensure correct return type
    return this.httpClient.put(url, data) as Observable<T>;
  }

  /**
   * Perform a DELETE request
   * @param url Endpoint URL
   * @returns Observable with the response
   */
  delete<T = any>(url: string): Observable<T> {
    // Use type assertion to ensure correct return type
    return this.httpClient.delete(url) as Observable<T>;
  }

  /**
   * Upload a file
   * @param file File to upload
   * @returns Promise with the record ID
   */
  uploadFile(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return new Promise(async (resolve): Promise<void> => {
      let data: any = (await this.httpClient
        .post('uploads/', formData)
        .toPromise()) as any;
      resolve(data.record);
    });
  }

  /**
   * Upload a public file
   * @param file File to upload
   * @returns Promise with the upload result
   */
  uploadPublicFile(file: File): Promise<{ success: boolean; record: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return new Promise(async (resolve): Promise<void> => {
      let data: any = (await this.httpClient
        .post('uploads/public', formData)
        .toPromise()) as any;
      resolve(data);
    });
  }

  /**
   * Upload a public file without authentication
   * @param file File to upload
   * @returns Promise with the upload result
   */
  async uploadPublicFileNoAuth(file: File): Promise<{ record: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return firstValueFrom<{ record: string }>(
      this.httpClient.post<{ record: string }>('uploads/open', formData),
    );
  }
}

/**
 * HTTP interceptor for handling authentication and error handling
 */
@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  constructor(
    public auth: AuthService,
    public alertService: AlertService,
    private authManagerService: AuthManagerService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const token = localStorage.getItem('token');

    return of(token).pipe(
      map((token) => {
        req = req.clone({
          headers: req.headers
            .set('Accept', 'application/json')
            .set('Accept-Language', 'es-mx'),
        });

        if (!req.url.includes('assets')) {
          const url = `${
            req.url.includes('http') ? req.url : environment.server + req.url
          }`.replace(/([^:]\/)\/+/g, '$1');
          req = req.clone({ url });
        }

        return token;
      }),
      concatMap((token) => {
        // Check for public routes first, regardless of authentication status
        if (req.url.includes('/public/')) {
          return next.handle(req);
        }

        if (token) {
          req = req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + token),
          });

          return next.handle(req);
        } else {
          return this.authManagerService
            .initializeAuth()
            .pipe(
              concatMap(({ authenticated }) =>
                authenticated ? this.intercept(req, next) : next.handle(req),
              ),
            );
        }
      }),
      catchError((error: HttpErrorResponse) =>
        this.handleErrorResponse(req, next, error),
      ),
    );
  }

  private handleErrorResponse(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    // Skip auth handling for assets and public routes
    if (req.url.includes('assets') || req.url.includes('/public/')) {
      return throwError(() => error);
    }

    return error.status === 401
      ? this.handleUnauthorizedError(req, next, error)
      : throwError(() => error);
  }

  private handleUnauthorizedError(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    return this.authManagerService.initializeAuth().pipe(
      concatMap(({ authenticated }) =>
        authenticated
          ? this.handleAuthenticatedRefresh(req, next, error)
          : this.handleAuthenticationFailed(req, error),
      ),
      catchError((refreshError: HttpErrorResponse) =>
        this.handleRefreshError(refreshError),
      ),
    );
  }

  private handleAuthenticatedRefresh(
    req: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    const newToken = localStorage.getItem('token');
    return newToken
      ? this.retryRequestWithNewToken(req, next, newToken)
      : this.handleMissingTokenAfterRefresh(originalError);
  }

  private retryRequestWithNewToken(
    req: HttpRequest<any>,
    next: HttpHandler,
    newToken: string,
  ): Observable<HttpEvent<any>> {
    const clonedReq = req.clone({
      headers: req.headers.set('Authorization', 'Bearer ' + newToken),
    });
    return next.handle(clonedReq);
  }

  private handleMissingTokenAfterRefresh(
    originalError: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    return throwError(() => originalError);
  }

  private handleAuthenticationFailed(
    req: HttpRequest<any>,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    // If authentication failed, redirect to login page to break the loop
    if (!req.url.includes('/auth/') && !req.url.includes('/public/')) {
      // Only redirect if not already on an auth page or public route
      setTimeout(() => {
        window.location.href = '/auth';
      }, 100);
    }
    return throwError(() => error);
  }

  private handleRefreshError(
    originalError: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    return throwError(() => originalError);
  }
}

export const DISABLE_ALERTS_INTERCEPTOR = new HttpContextToken<boolean>(
  () => false,
);
